import React, { useRef, useState } from "react";
import { Microphone, Stop, CircleNotch } from "@phosphor-icons/react";
import { transcribeSymptomAudio } from "@/lib/api";

/**
 * Record a short voice note and transcribe it to text via Whisper (Emergent LLM
 * key). Audio is sent in-flight only — never persisted client- or server-side.
 * Renders nothing when MediaRecorder / getUserMedia is unsupported.
 */
export default function VoiceNoteButton({ onText }) {
  const supported = typeof window !== "undefined"
    && !!navigator.mediaDevices?.getUserMedia
    && typeof window.MediaRecorder !== "undefined";

  const recRef = useRef(null);
  const [state, setState] = useState("idle"); // idle | recording | busy
  const [err, setErr] = useState("");

  if (!supported) return null;

  async function start() {
    setErr("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = window.MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new window.MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: mime || "audio/webm" });
        if (blob.size < 1200) { setState("idle"); return; } // too short to contain speech
        setState("busy");
        try {
          const { text } = await transcribeSymptomAudio(blob);
          if (text) onText(text);
          else setErr("No speech detected.");
        } catch (e2) {
          setErr(e2.response?.data?.detail || e2.message);
        } finally {
          setState("idle");
        }
      };
      rec.start();
      recRef.current = rec;
      setState("recording");
    } catch {
      setErr("Microphone unavailable or permission denied.");
    }
  }

  function stop() {
    try { recRef.current?.stop(); } catch { setState("idle"); }
  }

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      {state === "idle" && (
        <button type="button" className="voice-note-btn" onClick={start} data-testid="voice-note-btn" title="Dictate instead of typing">
          <Microphone size={12} weight="bold" /> VOICE NOTE
        </button>
      )}
      {state === "recording" && (
        <button type="button" className="voice-note-btn voice-note-btn--rec" onClick={stop} data-testid="voice-note-stop-btn">
          <Stop size={12} weight="fill" /> STOP &amp; TRANSCRIBE
        </button>
      )}
      {state === "busy" && (
        <span className="voice-note-btn" data-testid="voice-note-busy" style={{ cursor: "default" }}>
          <CircleNotch size={12} weight="bold" className="spin" /> Transcribing...
        </span>
      )}
      {err && <span style={{ fontSize: 10, color: "#C72E2E" }} data-testid="voice-note-error">{err}</span>}
    </span>
  );
}
